import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/utils";
import { useEffect } from "react";

interface TokenInfo {
	price: number;
	change24h: number | null;
	volume24h: number | null;
	marketCap: number | null;
	liquidity: number | null;
}

interface PriceData {
	sech: TokenInfo;
	eth: TokenInfo;
	usdc: TokenInfo;
}

const FALLBACK_PRICES: PriceData = {
	sech: { price: 0.024, change24h: null, volume24h: null, marketCap: null, liquidity: null },
	eth: { price: 0, change24h: null, volume24h: null, marketCap: null, liquidity: null },
	usdc: { price: 1, change24h: null, volume24h: null, marketCap: null, liquidity: null }
};

const CACHE_DURATION = 30_000;
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

let priceCache = {
	data: null as PriceData | null,
	timestamp: 0,
	error: null as Error | null
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function fetchPricesWithRetry(retries = MAX_RETRIES): Promise<PriceData> {
	try {
		const response = await apiFetch(`/v1/prices?t=${Date.now()}`);

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(
				errorData.message || `HTTP error! status: ${response.status}`
			);
		}

		const pricesResult = await response.json();

		return {
			eth: pricesResult.data.eth,
			usdc: pricesResult.data.usdc,
			sech: pricesResult.data.sech
		};
	} catch (error) {
		if (retries > 0) {
			await sleep(RETRY_DELAY);
			return fetchPricesWithRetry(retries - 1);
		}
		throw error;
	}
}

async function fetchCoinMarketCapPrices(): Promise<PriceData> {
	const now = Date.now();

	if (priceCache.data && now - priceCache.timestamp < CACHE_DURATION) {
		if (priceCache.error) {
			throw priceCache.error;
		}
		return priceCache.data;
	}

	try {
		const data = await fetchPricesWithRetry();
		priceCache = { data, timestamp: now, error: null };
		return data;
	} catch (error) {
		priceCache = {
			data: FALLBACK_PRICES,
			timestamp: now,
			error: error instanceof Error ? error : new Error('Failed to fetch prices')
		};
		console.error('Price fetching error:', error);
		throw error;
	}
}

export function useSechPrice() {
	const { toast } = useToast();

	const { data, isLoading, error } = useQuery({
		queryKey: ["pos-prices"],
		queryFn: fetchCoinMarketCapPrices,
		refetchInterval: 30000,
		staleTime: 25000,
		retry: 3,
		retryDelay: 2000,
		placeholderData: FALLBACK_PRICES
	});

	useEffect(() => {
		if (error && !isLoading) {
			const errorMessage =
				error instanceof Error ? error.message : "Failed to fetch prices";
			toast({
				title: "Price Update Notice",
				description: `Using fallback prices: ${errorMessage}`,
				variant: "destructive",
			});
		}
	}, [error, isLoading, toast]);

	const currentPrices = data || FALLBACK_PRICES;

	return {
		sechPrice: currentPrices.sech.price,
		prices: currentPrices,
		priceData: data,
		isLoading,
		error,
		lastUpdated: priceCache.timestamp,
	};
}
